import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, Trash2, Search } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';
import { getErrorMessage } from '@/lib/errors';

/* ── Tipos ── */
type TipoItem = 'servicio' | 'producto';
type ItemForm = {
  tipo: TipoItem;
  referenciaId: string;
  nombre: string;
  descripcion: string;
  cantidad: string;
  precioUnitario: string;
};

const TIPO_LABEL: Record<TipoItem, string> = {
  servicio: 'Servicio', producto: 'Producto',
};

const ESTADOS = [
  { value: 'borrador', label: 'Borrador' },
  { value: 'enviado',  label: 'Enviado al paciente' },
  { value: 'aceptado', label: 'Aceptado' },
  { value: 'en_curso', label: 'En curso' },
  { value: 'completado', label: 'Completado' },
  { value: 'cancelado',  label: 'Cancelado' },
];

const emptyItem = (): ItemForm => ({
  tipo: 'servicio', referenciaId: '', nombre: '',
  descripcion: '', cantidad: '1', precioUnitario: '',
});

const emptyForm = {
  titulo: '', descripcion: '', estado: 'borrador',
  vigenciaFin: '', notas: '',
  descuentoPorcentaje: '', descuentoMonto: '',
  tipoDescuento: 'ninguno' as 'ninguno' | 'porcentaje' | 'monto',
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pacienteId?: string;
  consultaId?: string;
  presupuesto?: any;
  onCreated?: (presupuesto: any) => void;
};

export function PresupuestoFormDialog({
  open, onOpenChange, pacienteId, consultaId, presupuesto, onCreated,
}: Props) {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const isEdit = !!presupuesto;

  const [form, setForm] = useState(emptyForm);
  const [selectedPacienteId, setSelectedPacienteId] = useState(pacienteId ?? '');
  const [selectedMedicoId, setSelectedMedicoId] = useState('');
  const [items, setItems] = useState<ItemForm[]>([emptyItem()]);
  const [searchTerms, setSearchTerms] = useState<Record<number, string>>({});

  /* ── Queries ── */
  const { data: pacientes = [] } = useQuery<any[]>({
    queryKey: ['pacientes-select'],
    queryFn: () => api.get('/api/pacientes').then(r => r.data),
    enabled: !pacienteId,
  });
  const { data: medicos = [] } = useQuery<any[]>({
    queryKey: ['medicos'],
    queryFn: () => api.get('/api/medicos').then(r => r.data),
    staleTime: 60_000,
  });
  const { data: servicios = [] } = useQuery<any[]>({
    queryKey: ['servicios'],
    queryFn: () => api.get('/api/servicios').then(r => r.data),
    staleTime: 60_000,
  });
  const { data: productos = [] } = useQuery<any[]>({
    queryKey: ['productos'],
    queryFn: () => api.get('/api/productos').then(r => r.data),
    staleTime: 60_000,
  });
  /* ── Inicializar ── */
  useEffect(() => {
    if (presupuesto) {
      setForm({
        titulo: presupuesto.titulo ?? '',
        descripcion: presupuesto.descripcion ?? '',
        estado: presupuesto.estado ?? 'borrador',
        vigenciaFin: presupuesto.vigenciaFin ? presupuesto.vigenciaFin.slice(0, 10) : '',
        notas: presupuesto.notas ?? '',
        descuentoPorcentaje: presupuesto.descuentoPorcentaje ? String(presupuesto.descuentoPorcentaje) : '',
        descuentoMonto: presupuesto.descuentoMonto ? String(presupuesto.descuentoMonto) : '',
        tipoDescuento: presupuesto.descuentoPorcentaje ? 'porcentaje'
          : presupuesto.descuentoMonto ? 'monto' : 'ninguno',
      });
      setSelectedPacienteId(presupuesto.pacienteId ?? '');
      setSelectedMedicoId(presupuesto.medicoId ?? '');
      setItems(
        presupuesto.items?.length
          ? presupuesto.items.map((i: any) => ({
              tipo: i.tipo,
              referenciaId: i.referenciaId ?? '',
              nombre: i.nombre,
              descripcion: i.descripcion ?? '',
              cantidad: String(i.cantidad),
              precioUnitario: String(i.precioUnitario),
            }))
          : [emptyItem()],
      );
    } else {
      setForm(emptyForm);
      setSelectedPacienteId(pacienteId ?? '');
      setSelectedMedicoId('');
      setItems([emptyItem()]);
      setSearchTerms({});
    }
  }, [presupuesto, open, pacienteId, usuario]);

  /* ── Helpers items ── */
  const updateItem = (idx: number, patch: Partial<ItemForm>) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));

  const selectCatalogo = (idx: number, tipo: TipoItem, item: any) => {
    const precio = item.precio ?? item.precioUnitario ?? 0;
    updateItem(idx, {
      referenciaId: item.id,
      nombre: item.nombre,
      descripcion: item.descripcion ?? '',
      precioUnitario: String(precio),
    });
    setSearchTerms(p => ({ ...p, [idx]: '' }));
  };

  const catalogoPorTipo = (tipo: TipoItem): any[] => {
    if (tipo === 'servicio') return servicios as any[];
    if (tipo === 'producto') return productos as any[];
    return [];
  };

  const filteredCatalogo = (idx: number, tipo: TipoItem) => {
    const term = (searchTerms[idx] ?? '').toLowerCase();
    return catalogoPorTipo(tipo).filter((i: any) =>
      !term || i.nombre?.toLowerCase().includes(term),
    );
  };

  /* ── Totales ── */
  const subtotal = items.reduce((s, it) => {
    return s + (parseFloat(it.cantidad) || 0) * (parseFloat(it.precioUnitario) || 0);
  }, 0);

  const descuento = (() => {
    if (form.tipoDescuento === 'porcentaje' && form.descuentoPorcentaje)
      return subtotal * (parseFloat(form.descuentoPorcentaje) / 100);
    if (form.tipoDescuento === 'monto' && form.descuentoMonto)
      return parseFloat(form.descuentoMonto);
    return 0;
  })();

  const total = Math.max(0, subtotal - descuento);

  /* ── Mutación ── */
  const mut = useMutation({
    mutationFn: (data: any) =>
      isEdit
        ? api.patch(`/api/presupuestos/${presupuesto.id}`, data).then(r => r.data)
        : api.post('/api/presupuestos', data).then(r => r.data),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['presupuestos'] });
      qc.invalidateQueries({ queryKey: ['presupuestos-paciente', selectedPacienteId] });
      toast.success(isEdit ? 'Presupuesto actualizado' : 'Presupuesto creado');
      onCreated?.(result);
      onOpenChange(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validItems = items.filter(it => it.nombre.trim());
    if (!validItems.length) {
      toast.error('Agrega al menos un item al presupuesto');
      return;
    }

    mut.mutate({
      titulo: form.titulo,
      descripcion: form.descripcion || null,
      estado: form.estado,
      vigenciaFin: form.vigenciaFin || null,
      notas: form.notas || null,
      pacienteId: selectedPacienteId,
      medicoId: selectedMedicoId,
      consultaId: consultaId ?? null,
      descuentoPorcentaje: form.tipoDescuento === 'porcentaje' && form.descuentoPorcentaje
        ? parseFloat(form.descuentoPorcentaje) : null,
      descuentoMonto: form.tipoDescuento === 'monto' && form.descuentoMonto
        ? parseFloat(form.descuentoMonto) : null,
      items: validItems.map(it => ({
        tipo: it.tipo,
        referenciaId: it.referenciaId || null,
        nombre: it.nombre,
        descripcion: it.descripcion || null,
        cantidad: parseFloat(it.cantidad) || 1,
        precioUnitario: parseFloat(it.precioUnitario) || 0,
      })),
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[94vh] overflow-y-auto focus:outline-none">
          <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between px-6 py-4 rounded-t-2xl z-10">
            <Dialog.Title className="text-lg font-semibold text-text-primary">
              {isEdit ? 'Editar presupuesto' : 'Nuevo presupuesto'}
            </Dialog.Title>
            <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={18} />
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                required value={form.titulo}
                onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))}
                placeholder="Ej. Plan de ortodoncia, Rehabilitación oral completa..."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>

            {/* Paciente + Médico */}
            <div className="grid grid-cols-2 gap-3">
              {!pacienteId && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Paciente <span className="text-red-500">*</span>
                  </label>
                  <select
                    required value={selectedPacienteId}
                    onChange={e => setSelectedPacienteId(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                  >
                    <option value="">Seleccionar</option>
                    {(pacientes as any[]).map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nombre} {p.apellidoPaterno}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className={pacienteId ? 'col-span-2' : ''}>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Médico <span className="text-red-500">*</span>
                </label>
                <select
                  required value={selectedMedicoId}
                  onChange={e => setSelectedMedicoId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                >
                  <option value="">Seleccionar médico</option>
                  {(medicos as any[]).map((m: any) => (
                    <option key={m.id} value={m.id}>
                      {m.nombre} {m.apellidoPaterno ?? ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Estado + Vigencia */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Estado</label>
                <select
                  value={form.estado}
                  onChange={e => setForm(p => ({ ...p, estado: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                >
                  {ESTADOS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Vigencia hasta</label>
                <input
                  type="date" value={form.vigenciaFin}
                  onChange={e => setForm(p => ({ ...p, vigenciaFin: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-text-primary">Conceptos</label>
                <button
                  type="button" onClick={() => setItems(p => [...p, emptyItem()])}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  <Plus size={13} /> Agregar línea
                </button>
              </div>

              <div className="space-y-3">
                {items.map((it, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-3 space-y-2.5 bg-gray-50/50">
                    {/* Fila 1: tipo + búsqueda en catálogo */}
                    <div className="flex items-center gap-2">
                      <select
                        value={it.tipo}
                        onChange={e => {
                          updateItem(idx, {
                            tipo: e.target.value as TipoItem,
                            referenciaId: '', nombre: '', precioUnitario: '',
                          });
                          setSearchTerms(p => ({ ...p, [idx]: '' }));
                        }}
                        className="px-2.5 py-2 rounded-lg border border-gray-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white w-32 flex-shrink-0"
                      >
                        {(Object.keys(TIPO_LABEL) as TipoItem[]).map(t => (
                          <option key={t} value={t}>{TIPO_LABEL[t]}</option>
                        ))}
                      </select>

                      <div className="relative flex-1">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          value={searchTerms[idx] ?? ''}
                          onChange={e => setSearchTerms(p => ({ ...p, [idx]: e.target.value }))}
                          placeholder={`Buscar ${TIPO_LABEL[it.tipo].toLowerCase()}...`}
                          className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-primary-300"
                        />
                        {searchTerms[idx] && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-40 overflow-y-auto">
                            {filteredCatalogo(idx, it.tipo).slice(0, 8).map((cat: any) => (
                              <button
                                key={cat.id} type="button"
                                onClick={() => selectCatalogo(idx, it.tipo, cat)}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center justify-between gap-2"
                              >
                                <span className="truncate">{cat.nombre}</span>
                                {cat.precio && (
                                  <span className="text-text-secondary flex-shrink-0">
                                    {formatCurrency(cat.precio)}
                                  </span>
                                )}
                              </button>
                            ))}
                            {filteredCatalogo(idx, it.tipo).length === 0 && (
                              <p className="px-3 py-2 text-xs text-text-secondary">Sin resultados</p>
                            )}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => setItems(p => p.filter((_, i) => i !== idx))}
                        disabled={items.length === 1}
                        className="p-1.5 text-gray-300 hover:text-red-400 disabled:opacity-20 flex-shrink-0"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* Fila 2: nombre + cant + precio */}
                    <div className="grid grid-cols-12 gap-2">
                      <input
                        required={idx === 0}
                        value={it.nombre}
                        onChange={e => updateItem(idx, { nombre: e.target.value })}
                        placeholder="Nombre / descripción"
                        className="col-span-6 px-2.5 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-primary-300"
                      />
                      <input
                        type="number" min="0.01" step="0.01"
                        value={it.cantidad}
                        onChange={e => updateItem(idx, { cantidad: e.target.value })}
                        className="col-span-2 px-2 py-2 rounded-lg border border-gray-200 text-xs text-center focus:outline-none focus:ring-2 focus:ring-primary-300"
                      />
                      <input
                        type="number" min="0" step="0.01"
                        value={it.precioUnitario}
                        onChange={e => updateItem(idx, { precioUnitario: e.target.value })}
                        placeholder="Precio"
                        className="col-span-3 px-2.5 py-2 rounded-lg border border-gray-200 text-xs text-right focus:outline-none focus:ring-2 focus:ring-primary-300"
                      />
                      <p className="col-span-1 text-[10px] text-text-secondary text-right self-center">
                        {formatCurrency((parseFloat(it.cantidad) || 0) * (parseFloat(it.precioUnitario) || 0))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Descuento */}
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-text-primary">Descuento</p>
                <select
                  value={form.tipoDescuento}
                  onChange={e => setForm(p => ({
                    ...p,
                    tipoDescuento: e.target.value as any,
                    descuentoPorcentaje: '', descuentoMonto: '',
                  }))}
                  className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none bg-white"
                >
                  <option value="ninguno">Sin descuento</option>
                  <option value="porcentaje">Por porcentaje (%)</option>
                  <option value="monto">Monto fijo ($)</option>
                </select>
              </div>

              {form.tipoDescuento === 'porcentaje' && (
                <input
                  type="number" min="0" max="100" step="0.01"
                  value={form.descuentoPorcentaje}
                  onChange={e => setForm(p => ({ ...p, descuentoPorcentaje: e.target.value }))}
                  placeholder="Ej. 10 (para 10%)"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              )}
              {form.tipoDescuento === 'monto' && (
                <input
                  type="number" min="0" step="0.01"
                  value={form.descuentoMonto}
                  onChange={e => setForm(p => ({ ...p, descuentoMonto: e.target.value }))}
                  placeholder="Ej. 500.00"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              )}

              {/* Totales */}
              <div className="space-y-1 pt-1 border-t border-gray-100">
                <div className="flex justify-between text-sm text-text-secondary">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                {descuento > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Descuento</span>
                    <span>- {formatCurrency(descuento)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-text-primary text-base pt-1">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
            </div>

            {/* Notas */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Notas adicionales</label>
              <textarea
                rows={2} value={form.notas}
                onChange={e => setForm(p => ({ ...p, notas: e.target.value }))}
                placeholder="Condiciones, aclaraciones, términos del presupuesto..."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              />
            </div>

            {/* Acciones */}
            <div className="flex gap-3 pt-2">
              <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                Cancelar
              </Dialog.Close>
              <button
                type="submit" disabled={mut.isPending}
                className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {mut.isPending ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear presupuesto'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
